import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Search, AddCircleOutline, Visibility, ArrowBack, ExitToApp as ExitToAppIcon, Info as InfoIcon } from '@mui/icons-material';
import api from '../api/api'; 
import dayjs from 'dayjs';
import NuevoIngresoModal from '../components/hospitalizacion/NuevoIngresoModal';
import EstadoAltaModal from '../components/hospitalizacion/EstadoAltaModal';
import DetalleAltaModal from '../components/hospitalizacion/DetalleAltaModal';

const HospitalizadosLista = () => {
  const navigate = useNavigate();

  // --- ESTADOS ---
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estado inicial por defecto: 'activo'
  const [estadoFiltro, setEstadoFiltro] = useState('activo');

  const [userRol, setUserRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);
  const [todasSucursales, setTodasSucursales] = useState([]); 
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(''); 
  
  const [openNuevoModal, setOpenNuevoModal] = useState(false);
  const [openDetalleModal, setOpenDetalleModal] = useState(false);
  const [selectedHosp, setSelectedHosp] = useState(null);
  const [openEstadoModal, setOpenEstadoModal] = useState(false);

  // --- LÓGICA DE CARGA ---

  // 1. Cargar configuración inicial (Token y Sucursales)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRol(payload.id_rol || null);

        const sucursalesPermitidas = payload.sucursales || [];
        setUserSucursales(Array.isArray(sucursalesPermitidas) ? sucursalesPermitidas : []);

        // Cargar catálogo de sucursales si es admin
        api.get('/sucursales/')
          .then(res => {
            const data = Array.isArray(res.data) ? res.data : [];
            const unicas = data.filter(
              (s, index, self) =>
                s?.id_sucursal &&
                index === self.findIndex(x => x.id_sucursal === s.id_sucursal)
            );
            setTodasSucursales(unicas);
          })
          .catch(err => console.error('Error cargando sucursales:', err));

        // Determinar sucursal por defecto
        let sucursalDefecto = '';
        if (payload.id_sucursal && payload.id_sucursal > 0) {
          sucursalDefecto = payload.id_sucursal;
        } else if (sucursalesPermitidas.length > 0) {
          sucursalDefecto = sucursalesPermitidas[0];
        }
        setSucursalSeleccionada(sucursalDefecto);

      } catch (err) {
        console.error('Error decodificando token:', err);
      }
    }
  }, []);

  // 2. Cargar datos de la API 
  // Se dispara cuando cambie el Rol, la Sucursal o el Filtro de Estado
  useEffect(() => {
    if (userRol !== null) {
      fetchHospitalizados();
    }
    // eslint-disable-next-line
  }, [userRol, sucursalSeleccionada, estadoFiltro]);

  const fetchHospitalizados = async () => {
    try {
        setLoading(true);
        const params = { 
            limit: 100,
            estado: estadoFiltro 
        };

        // Solo enviamos sucursal_id si el usuario seleccionó una específica
        if (sucursalSeleccionada) {
            params.sucursal_id = sucursalSeleccionada;
        }

        const res = await api.get('/hospitalizaciones/', { params });
        setPacientes(res.data);
    } catch (err) {
        console.error('Error cargando hospitalizados:', err);
    } finally {
        setLoading(false);
    }
};

  // --- HANDLERS ---

  const handleOpenEstado = (hosp) => {
    setSelectedHosp(hosp);
    setOpenEstadoModal(true);
  };

  const handleOpenDetalle = async (hosp) => {
    try {
      const res = await api.get(`/hospitalizaciones/${hosp.id_hospitalizacion}`);
      setSelectedHosp(res.data);
      setOpenDetalleModal(true);
    } catch (err) {
      console.error("Error al obtener el detalle:", err);
    }
  };

  const getColorEstado = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'activo': return 'success';
      case 'alta': return 'info';
      case 'fallecido': return 'error';
      case 'transferido': return 'warning';
      default: return 'default';
    }
  };

  // Filtrado por búsqueda local
  const filtered = pacientes.filter(p => {
    const term = search.toLowerCase();
    return (
      (p.nombre_paciente || '').toLowerCase().includes(term) ||
      (p.motivo_ingreso || '').toLowerCase().includes(term) ||
      (p.veterinario_responsable || '').toLowerCase().includes(term)
    );
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack/>} onClick={() => navigate('/clientes')} variant="text" sx={{ mb: 2 }}>
        Volver
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {estadoFiltro === 'activo' ? 'Pacientes Hospitalizados Activos' : 'Historial de Hospitalizaciones'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Selector de Estado (Filtro Principal) */}
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Estado</InputLabel>
            <Select
              value={estadoFiltro}
              label="Estado"
              onChange={(e) => {
                setEstadoFiltro(e.target.value);
                setPage(0); // Resetear paginación al cambiar filtro
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="activo">Activos</MenuItem>
              <MenuItem value="alta">Altas</MenuItem>
              <MenuItem value="fallecido">Fallecidos</MenuItem>
              <MenuItem value="transferido">Transferidos</MenuItem>
            </Select>
          </FormControl>

          {/* Selector de Sucursal */}
          {todasSucursales.length > 0 && (
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Sucursal</InputLabel>
              <Select
                value={sucursalSeleccionada}
                label="Sucursal"
                onChange={(e) => setSucursalSeleccionada(e.target.value)}
              >
                <MenuItem value="">Todas las permitidas</MenuItem>
                {todasSucursales.map(s => (
                  <MenuItem key={s.id_sucursal} value={s.id_sucursal}>
                    {s.nombre_sucursal}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={() => setOpenNuevoModal(true)}
          >
            Nuevo Ingreso
          </Button>
        </Box>
      </Box>

      {/* Barra de Búsqueda */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar por nombre, motivo o veterinario..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Especie / Raza</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Motivo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Veterinario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha Ingreso</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estancia</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length > 0 ? paginated.map((row) => {
                  const dias = row.fecha_ingreso
                    ? dayjs().diff(dayjs(row.fecha_ingreso), 'day')
                    : 0;
                  return (
                    <TableRow key={row.id_hospitalizacion} hover>
                      <TableCell>{row.nombre_paciente || '—'}</TableCell>
                      <TableCell>{row.especie || '—'} / {row.raza || '—'}</TableCell>
                      <TableCell>{row.motivo_ingreso || '—'}</TableCell>
                      <TableCell>{row.veterinario_responsable || 'No asignado'}</TableCell>
                      <TableCell>
                        {row.fecha_ingreso ? dayjs(row.fecha_ingreso).format('DD/MM/YYYY hh:mm A') : '—'}
                        
                      </TableCell>
                      <TableCell>{dias} {dias === 1 ? 'día' : 'días'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.estado?.toUpperCase() || '—'}
                          color={getColorEstado(row.estado)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          {row.estado === 'activo' ? (
                            <Tooltip title="Registrar Salida / Alta">
                              <IconButton color="warning" onClick={() => handleOpenEstado(row)} size="small">
                                <ExitToAppIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Ver Resumen de Egreso">
                              <IconButton 
                                sx={{ color: '#0288d1' }} 
                                onClick={() => handleOpenDetalle(row)}
                                size="small"
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Ver ficha completa">
                            <IconButton color="primary" onClick={() => navigate(`/hospitalizacion/${row.id_hospitalizacion}`)} size="small">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      No se encontraron pacientes con el estado seleccionado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      {/* MODALES */}
      <DetalleAltaModal
        open={openDetalleModal}
        onClose={() => { setOpenDetalleModal(false); setSelectedHosp(null); }}
        hospitalizacion={selectedHosp}
      />

      <NuevoIngresoModal
        open={openNuevoModal}
        onClose={() => setOpenNuevoModal(false)}
        onSuccess={fetchHospitalizados}
      />

      {selectedHosp && (
        <EstadoAltaModal
          open={openEstadoModal}
          onClose={() => { setOpenEstadoModal(false); setSelectedHosp(null); }}
          hospitalizacion={selectedHosp}
          onSuccess={fetchHospitalizados}
        />
      )}
    </Box>
  );
};

export default HospitalizadosLista;